/**
 * personal_presets.ts — PURE definitions of the personal-world STYLES
 * (2026-08-30): blank (the raw canvas), a cliffside sea MANSION, a city
 * PENTHOUSE, and a COTTAGE in a field. Deterministic constants — every
 * client renders the identical world; the server stores only the choice
 * (world_preset row).
 *
 * Grounds are ANALYTIC (the merchant-hive doctrine): presetGroundY is the
 * single truth the KCC clamp, the terrain mesh, and every placement share.
 * Design research 2026-08-30: cliff mansions cascade glass volumes toward an
 * infinity pool hung over the slope; penthouses live on height/light/scale
 * with a wraparound sky terrace; cottages are whitewash + thatch behind a
 * picket fence with wildflower drifts.
 */

export type WorldPresetKey = 'blank' | 'mansion' | 'penthouse' | 'cottage';

export const WORLD_PRESETS: Array<{ key: WorldPresetKey; name: string; desc: string }> = [
  { key: 'blank', name: 'Blank Canvas', desc: 'The raw eroded island — yours to sculpt and build.' },
  { key: 'mansion', name: 'Cliffside Mansion', desc: 'Glass volumes over the sea, an infinity pool hung on the cliff edge.' },
  { key: 'penthouse', name: 'City Penthouse', desc: 'A glass pavilion and sky terrace above a glowing skyline.' },
  { key: 'cottage', name: 'Field Cottage', desc: 'Whitewash and thatch behind a picket fence, wildflowers to the horizon.' },
];

/** Where each preset's HOME stands (door on local -Z; groundY = pad height). */
export const PRESET_HOME: Record<Exclude<WorldPresetKey, 'blank'>, { x: number; z: number; yaw: number; w: number; d: number; h: number; groundY: number }> = {
  mansion: { x: 14, z: 0, yaw: Math.PI / 2, w: 24, d: 14, h: 9, groundY: 16 },  // door faces inland (-X)
  penthouse: { x: 0, z: 2, yaw: 0, w: 18, d: 12, h: 5.2, groundY: 92 },          // door opens onto the -Z terrace
  cottage: { x: 0, z: 4, yaw: 0, w: 10, d: 9, h: 5.5, groundY: 0.9 },            // door faces the garden path (-Z)
};

export const MANSION_CLIFF_X0 = 40;   // the plateau's edge
export const MANSION_CLIFF_X1 = 64;   // the cliff's foot (the beach begins)
export const MANSION_BEACH_X1 = 86;   // the beach meets the shallows
export const MANSION_SEA_Y = 0;       // sea surface — REAL, swimmable
/** The carved sand ramp down the cliff (walkable corridor, z band). */
export const MANSION_RAMP_Z0 = 20;
export const MANSION_RAMP_Z1 = 34;
export const PENT_DECK_HW = 20;       // deck half extents
export const PENT_DECK_HD = 16;
export const PENT_DECK_Y = 92;

const smooth = (t: number): number => { const c = Math.max(0, Math.min(1, t)); return c * c * (3 - 2 * c); };

/** The analytic ground for a styled personal world. */
export function presetGroundY(preset: WorldPresetKey, x: number, z: number): number {
  if (preset === 'mansion') {
    // A garden plateau, a cliff, a BEACH at its foot, shallows, open sea —
    // with a carved sand ramp (the walkable way down to the water).
    const plateau = 16 + 0.35 * Math.sin(x * 0.05) * Math.cos(z * 0.06);
    const beachAtFoot = 1.4 + 0.1 * Math.sin(z * 0.08);
    let h: number;
    if (x <= MANSION_CLIFF_X0) h = plateau;
    else if (x <= MANSION_CLIFF_X1) {
      const s = smooth((x - MANSION_CLIFF_X0) / (MANSION_CLIFF_X1 - MANSION_CLIFF_X0));
      h = plateau * (1 - s) + beachAtFoot * s;
    } else if (x <= MANSION_BEACH_X1) {
      const s = (x - MANSION_CLIFF_X1) / (MANSION_BEACH_X1 - MANSION_CLIFF_X1);
      h = beachAtFoot * (1 - s) + -1.6 * s;           // sand crosses the waterline
    } else {
      const s = smooth((x - MANSION_BEACH_X1) / 34);
      h = -1.6 * (1 - s) + -6.5 * s;                  // shallows deepen to the floor
    }
    // The RAMP corridor: an even, walkable grade from the terrace to the sand.
    if (z >= MANSION_RAMP_Z0 && z <= MANSION_RAMP_Z1 && x > 36 && x <= MANSION_CLIFF_X1) {
      const t = (x - 36) / (MANSION_CLIFF_X1 - 36);
      const rampH = 16 * (1 - t) + beachAtFoot * t;
      const zEdge = Math.min((z - MANSION_RAMP_Z0) / 2.5, (MANSION_RAMP_Z1 - z) / 2.5, 1);
      h = h * (1 - zEdge) + rampH * zEdge;
    }
    // The mansion pad sits perfectly level.
    const hp = PRESET_HOME.mansion;
    const pad = smooth(1 - Math.hypot(x - hp.x, z - hp.z) / 22);
    h = h * (1 - pad) + hp.groundY * pad;
    return h;
  }
  if (preset === 'penthouse') {
    // The tower deck high above the street grid.
    return Math.abs(x) <= PENT_DECK_HW && Math.abs(z) <= PENT_DECK_HD ? PENT_DECK_Y : 0;
  }
  if (preset === 'cottage') {
    // A gently rolling meadow with a level dooryard.
    let h = 0.6 + 1.1 * Math.sin(x * 0.027) * Math.cos(z * 0.031) + 0.5 * Math.sin(x * 0.013 + z * 0.017);
    const hp = PRESET_HOME.cottage;
    const pad = smooth(1 - Math.hypot(x - hp.x, z - hp.z) / 14);
    h = h * (1 - pad) + hp.groundY * pad;
    return h;
  }
  return 0; // blank never routes here
}
